import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

// Neumorphic Design System
const nm = {
  bg: '#e8ecf1',
  shadow: '8px 8px 16px #c5c9cd, -8px -8px 16px #ffffff',
  shadowSm: '4px 4px 8px #c5c9cd, -4px -4px 8px #ffffff',
  shadowInset: 'inset 4px 4px 8px #c5c9cd, inset -4px -4px 8px #ffffff',
  shadowLg: '12px 12px 24px #c5c9cd, -12px -12px 24px #ffffff',
};

const NmCard = ({ children, span = "1", style = {}, hover = true }) => {
  const [mounted, setMounted] = React.useState(false);
  
  React.useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div
      style={{
        gridColumn: `span ${span}`,
        padding: '24px',
        borderRadius: '16px',
        background: nm.bg,
        boxShadow: nm.shadowSm,
        transition: 'all 0.3s ease',
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(20px)',
        cursor: hover ? 'pointer' : 'default',
        ...style
      }}
      onMouseEnter={hover ? (e) => {
        e.currentTarget.style.boxShadow = nm.shadow;
        e.currentTarget.style.transform = 'translateY(-2px)';
      } : undefined}
      onMouseLeave={hover ? (e) => {
        e.currentTarget.style.boxShadow = nm.shadowSm;
        e.currentTarget.style.transform = 'translateY(0)';
      } : undefined}
    >{children}</div>
  );
};

export function DashboardViewNew({ stats, models }) {
  const totalModels = models?.length || 0;
  const activeModels = models?.filter(m => m.status === 'active').length || 0;
  const chatModels = models?.filter(m => m.function === 'Chat').length || 0;
  const imageModels = models?.filter(m => m.function === 'Image').length || 0;
  const videoModels = models?.filter(m => m.function === 'Video').length || 0;
  const musicModels = models?.filter(m => m.function === 'Music').length || 0;

  // Provider credits state
  const [providerCredits, setProviderCredits] = useState({
    openrouter: null,
    keiai: null,
    loading: true,
    error: null
  });

  // Fetch provider credits on mount
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const adminToken = process.env.REACT_APP_ADMIN_SECRET_KEY || localStorage.getItem('adminToken');
        
        // Fetch both providers in parallel
        const [orRes, keiRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/provider-credits/openrouter`, {
            headers: { 'x-admin-token': adminToken }
          }).catch(() => null),
          fetch(`${API_URL}/api/admin/provider-credits/keiai`, {
            headers: { 'x-admin-token': adminToken }
          }).catch(() => null)
        ]);

        const openrouterData = orRes?.ok ? await orRes.json() : null;
        const keiaiData = keiRes?.ok ? await keiRes.json() : null;

        setProviderCredits({
          openrouter: openrouterData?.success ? openrouterData : null,
          keiai: keiaiData?.success ? keiaiData : null,
          loading: false,
          error: null
        });
      } catch (err) {
        setProviderCredits(prev => ({ ...prev, loading: false, error: err.message }));
      }
    };

    fetchCredits();
    // Refresh every 60 seconds
    const interval = setInterval(fetchCredits, 60000);
    return () => clearInterval(interval);
  }, []);

  const recentActivities = [
    { user: 'Admin', action: 'Added new model: GPT-4o', time: '2 min ago', color: '#6366f1' },
    { user: 'System', action: 'Synced OpenRouter models', time: '15 min ago', color: '#8b5cf6' },
    { user: 'Admin', action: 'Updated model status', time: '1 hour ago', color: '#ec4899' },
    { user: 'System', action: 'Database backup completed', time: '2 hours ago', color: '#10b981' },
    { user: 'Admin', action: 'Added Kei.ai models', time: '3 hours ago', color: '#f59e0b' },
  ];

  return (
    <div>
      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '20px', marginBottom: '24px' }}>
        {/* Total Models */}
        <NmCard span="3" hover={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' }}>
              <svg className="w-8 h-8" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Total Models</div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a' }}>{totalModels}</div>
            </div>
          </div>
        </NmCard>

        {/* Active Models */}
        <NmCard span="3" hover={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)' }}>
              <svg className="w-8 h-8" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Active Models</div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a' }}>{activeModels}</div>
            </div>
          </div>
        </NmCard>

        {/* Chat Models */}
        <NmCard span="3" hover={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}>
              <svg className="w-8 h-8" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Chat Models</div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a' }}>{chatModels}</div>
            </div>
          </div>
        </NmCard>

        {/* Media Models */}
        <NmCard span="3" hover={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #ec4899, #db2777)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)' }}>
              <svg className="w-8 h-8" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Media Models</div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a' }}>{imageModels + videoModels + musicModels}</div>
            </div>
          </div>
        </NmCard>

        {/* Model Distribution */}
        <NmCard span="6" hover={false}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Model Distribution</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>Breakdown by function type</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { label: 'Chat', count: chatModels, total: totalModels, color: '#6366f1' },
              { label: 'Image', count: imageModels, total: totalModels, color: '#3b82f6' },
              { label: 'Video', count: videoModels, total: totalModels, color: '#8b5cf6' },
              { label: 'Music', count: musicModels, total: totalModels, color: '#10b981' },
            ].map((item, idx) => {
              const percentage = totalModels > 0 ? (item.count / totalModels * 100).toFixed(1) : 0;
              return (
                <div key={idx}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{item.label}</span>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#64748b' }}>{item.count} ({percentage}%)</span>
                  </div>
                  <div style={{ width: '100%', height: '8px', background: nm.bg, borderRadius: '100px', boxShadow: nm.shadowInset, overflow: 'hidden' }}>
                    <div style={{ width: `${percentage}%`, height: '100%', background: `linear-gradient(90deg, ${item.color}, ${item.color}dd)`, borderRadius: '100px', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </NmCard>

        {/* Provider Credits - Real-time */}
        <NmCard span="6" hover={false}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Provider Credits</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>Real-time balance (auto-refresh)</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* OpenRouter Credits */}
            <div style={{ padding: '12px', borderRadius: '12px', background: nm.bg, boxShadow: nm.shadowInset }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg className="w-4 h-4" fill="#fff" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v2h2v-2zm0-8H9v6h2V5z"/></svg>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>OpenRouter</span>
                </div>
                {providerCredits.loading && (
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Loading...</span>
                )}
                {!providerCredits.loading && providerCredits.openrouter && (
                  <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '600' }}>
                    {typeof providerCredits.openrouter.balance === 'number' 
                      ? `$${providerCredits.openrouter.balance.toFixed(2)}` 
                      : 'N/A'}
                  </span>
                )}
              </div>
              {!providerCredits.loading && providerCredits.openrouter && (
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#64748b' }}>
                  <span>Limit: ${providerCredits.openrouter.limit?.toFixed?.(2) || 'N/A'}</span>
                  <span>Used: ${providerCredits.openrouter.usage?.toFixed?.(2) || '0.00'}</span>
                  {providerCredits.openrouter.isFreeTier && <span style={{ color: '#f59e0b' }}>Free Tier</span>}
                </div>
              )}
              {!providerCredits.loading && !providerCredits.openrouter && (
                <span style={{ fontSize: '11px', color: '#ef4444' }}>Failed to load</span>
              )}
            </div>

            {/* Kei.AI Credits */}
            <div style={{ padding: '12px', borderRadius: '12px', background: nm.bg, boxShadow: nm.shadowInset }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg className="w-4 h-4" fill="#fff" viewBox="0 0 20 20"><path d="M13 7H7v6h6V7z"/><path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd"/></svg>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>Kei.AI</span>
                </div>
                {providerCredits.loading && (
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>Loading...</span>
                )}
                {!providerCredits.loading && providerCredits.keiai && (
                  <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '600' }}>
                    {typeof providerCredits.keiai.balance === 'number' 
                      ? `${providerCredits.keiai.balance.toLocaleString()} credits` 
                      : providerCredits.keiai.balance || 'Check Dashboard'}
                  </span>
                )}
              </div>
              {!providerCredits.loading && providerCredits.keiai?.note && (
                <span style={{ fontSize: '11px', color: '#64748b' }}>{providerCredits.keiai.note}</span>
              )}
              {!providerCredits.loading && !providerCredits.keiai && (
                <span style={{ fontSize: '11px', color: '#ef4444' }}>Failed to load</span>
              )}
            </div>

            {/* Last Updated */}
            {!providerCredits.loading && (providerCredits.openrouter || providerCredits.keiai) && (
              <div style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'right' }}>
                Last updated: {new Date(providerCredits.openrouter?.lastUpdated || providerCredits.keiai?.lastUpdated || Date.now()).toLocaleTimeString()}
                <span style={{ marginLeft: '8px', color: '#64748b' }}>(refreshes every 60s)</span>
              </div>
            )}
          </div>
        </NmCard>

        {/* Recent Activity */}
        <NmCard span="6" hover={false}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Recent Activity</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>Latest admin actions</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {recentActivities.map((activity, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', borderRadius: '10px', background: nm.bg, boxShadow: nm.shadowInset }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: `linear-gradient(135deg, ${activity.color}, ${activity.color}dd)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: '700', flexShrink: 0 }}>
                  {activity.user[0]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activity.user}</div>
                  <div style={{ fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activity.action}</div>
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500', whiteSpace: 'nowrap' }}>{activity.time}</div>
              </div>
            ))}
          </div>
        </NmCard>

        {/* Quick Actions */}
        <NmCard span="12" hover={false}>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>Quick Actions</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>Common administrative tasks</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {[
              { icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', label: 'Add Model', color: '#6366f1' },
              { icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', label: 'Sync Models', color: '#3b82f6' },
              { icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', label: 'Export Data', color: '#8b5cf6' },
              { icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', label: 'Settings', color: '#10b981' },
            ].map((action, idx) => (
              <button
                key={idx}
                style={{
                  padding: '20px',
                  background: nm.bg,
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: nm.shadowSm,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = nm.shadow;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = nm.shadowSm;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: `linear-gradient(135deg, ${action.color}, ${action.color}dd)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${action.color}40` }}>
                  <svg className="w-6 h-6" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d={action.icon} clipRule="evenodd"/></svg>
                </div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{action.label}</span>
              </button>
            ))}
          </div>
        </NmCard>
      </div>
    </div>
  );
}
